import { useState } from 'react'
import { AlbumCover } from '@/components/AlbumCover'
import { albumPhotoUrl } from '@/lib/albums'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'

// An album's cover that can be swiped sideways through a few of its photos without opening it, as Pinterest does,
// with a dot for each under it. The collage comes first. It scrolls natively with snap points, so the browser keeps
// an upward or downward swipe for the page; a tap anywhere on it still opens the album (it sits in the card's link).
export function AlbumPeek({ covers, preview, index }: { covers: string[]; preview: string[]; index: number }) {
  const [at, setAt] = useState(0)
  const cover = <AlbumCover covers={covers} index={index} large={(id) => albumPhotoUrl(id, 'medium')} />
  // One photo has nothing more to show than its cover.
  if (preview.length < 2) return cover
  const slides = preview.length + 1
  return (
    <span className="flex flex-col gap-2">
      <span
        data-testid="album-peek"
        onScroll={(e) => {
          const el = e.currentTarget
          setAt(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)))
        }}
        className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain rounded-[1.75rem] [&::-webkit-scrollbar]:hidden"
      >
        <span className="w-full shrink-0 snap-center">{cover}</span>
        {preview.map((id, i) => (
          <span key={id} aria-hidden="true" className="w-full shrink-0 snap-center">
            <img
              src={albumPhotoUrl(id, 'thumb')}
              srcSet={`${albumPhotoUrl(id, 'thumb')} 480w, ${albumPhotoUrl(id, 'medium')} 1200w`}
              sizes="(min-width: 1024px) 18rem, (min-width: 640px) 30vw, 45vw"
              alt=""
              // Fetched as the card nears the screen, not as it's swiped to, so the slides don't arrive blank.
              loading="lazy"
              decoding="async"
              className={cn('block aspect-[4/5] w-full rounded-[1.75rem] object-cover', tint(index + i))}
            />
          </span>
        ))}
      </span>
      <span aria-hidden="true" data-testid="album-peek-dots" className="flex justify-center gap-1.5">
        {Array.from({ length: slides }, (_, i) => (
          <span
            key={i}
            data-current={i === at || undefined}
            className="size-1.5 rounded-full bg-foreground/20 transition-colors data-current:bg-foreground/70"
          />
        ))}
      </span>
    </span>
  )
}
